import { ActionCreator } from 'redux';
import {
    ChannelEvent,
    DeliveryLogType,
    EventLog,
    LinkContacts,
    LinksClicked,
    Log,
    LogSummary,
    ReportLocationStats,
    TemplateLink,
    TrackedLink,
} from '@dev/ee-api/ee-api-models';

import {
    ExportEventsParams,
    ExportLinkContactsParams,
    ExportLinksParams,
    LinkContactsParams,
    ReportsSummaryParams,
    ResendParams,
} from '@/services/ElasticApi';
import {
    ClearCampaignsLinkTrackingAction,
    ClearCampaignsReportsDataAction,
    ClearCampaignsReportsSummaryAction,
    ClearLogFromReportsAction,
    ContactLinksFetchErrorAction,
    ContactLinksFetchStartAction,
    ContactLinksFetchSuccessAction,
    EventLogByChannelNameErrorAction,
    EventLogByChannelNameStartAction,
    EventLogByChannelNameSuccessAction,
    EventLogErrorAction,
    EventLogParams,
    EventLogParamsByChannelName,
    EventLogStartAction,
    EventLogSuccessAction,
    EventLogTotalEventCounts,
    EventLogTotalResultCounts,
    ExportEventsAction,
    ExportLinkContactsAction,
    ExportLinksAction,
    FetchChannelEventsCountsAction,
    GetLinkTrackingAbSplitErrorAction,
    GetLinkTrackingAbSplitStartAction,
    GetLinkTrackingAbSplitSuccessAction,
    GetLinkTrackingLisErrorAction,
    GetLinkTrackingListStartAction,
    GetLinkTrackingListSuccessAction,
    GetReportsLocationStatsByNameStartAction,
    GetReportsLocationStatsByNameSuccessAction,
    GetReportsSummaryErrorAction,
    GetReportsSummaryStartAction,
    GetReportsSummarySuccessAction,
    LinkContactsAllErrorAction,
    LinkContactsAllStartAction,
    LinkContactsAllSuccessAction,
    LinkContactsErrorAction,
    LinkContactsStartAction,
    LinkContactsSuccessAction,
    LinktrackingsAbsplitActionParams,
    LocationReportsInterface,
    LogParams,
    PaginationInterface,
    ReportsActionTypes,
    ReportsFetchErrorAction,
    ReportsFetchStartAction,
    ReportsFetchSuccessAction,
    ReportsState,
    ResendErrorAction,
    ResendStartAction,
    ResendSuccessAction,
    UpdateChannelEventsCountsAction,
} from './types';
import { SingleContactPaginationInterface } from '../contacts/types';
import { CampaignsActionTypes } from '../campaigns/types';
import { ApplicationState } from '..';

export const ReportsLoadStart: ActionCreator<ReportsFetchStartAction> = (
    params: LogParams
): ReportsFetchStartAction => ({
    type: ReportsActionTypes.FETCH_START,
    payload: {
        params,
        logsLoading: true,
        statuses: params.statuses,
    },
});

export const ReportsLoadError: ActionCreator<ReportsFetchErrorAction> = (): ReportsFetchErrorAction => ({
    type: ReportsActionTypes.FETCH_ERROR,
    payload: {
        logsLoading: false,
    },
});

export const ReportsLoadSuccess: ActionCreator<ReportsFetchSuccessAction> = (
    log: Log,
    logsCount: number,
    statuses: DeliveryLogType[],
    hasMoreLogs: boolean
): ReportsFetchSuccessAction => ({
    type: ReportsActionTypes.FETCH_SUCCESS,
    payload: {
        log,
        logsCount,
        logsLoading: false,
        statuses,
        hasMoreLogs,
    },
});

export const EventLogStart: ActionCreator<EventLogStartAction> = (params: EventLogParams): EventLogStartAction => ({
    type: ReportsActionTypes.EVENT_LOG_START,
    payload: {
        params,
        eventLogLoading: true,
    },
});

export const EventLogError: ActionCreator<EventLogErrorAction> = (): EventLogErrorAction => ({
    type: ReportsActionTypes.EVENT_LOG_ERROR,
    payload: {
        eventLogLoading: false,
    },
});

export const EventLogSuccess: ActionCreator<EventLogSuccessAction> = (
    eventLog: EventLog,
    eventsCount: EventLogTotalEventCounts,
    resultsCount: EventLogTotalResultCounts,
    eventLogParams: EventLogParams
): EventLogSuccessAction => ({
    type: ReportsActionTypes.EVENT_LOG_SUCCESS,
    payload: {
        eventLog,
        eventsCount,
        resultsCount,
        eventLogLoading: false,
        eventLogParams,
    },
});

export const EventLogByChannelNameStart: ActionCreator<EventLogByChannelNameStartAction> = (
    params: EventLogParamsByChannelName
): EventLogByChannelNameStartAction => ({
    type: ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_START,
    payload: {
        params,
        eventLogByChannelNameLoading: true,
    },
});

export const EventLogByChannelNameError: ActionCreator<
    EventLogByChannelNameErrorAction
> = (): EventLogByChannelNameErrorAction => ({
    type: ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_ERROR,
    payload: {
        eventLogByChannelNameLoading: false,
    },
});

export const EventLogByChannelNameSuccess = (
    eventLogByChannelName: ChannelEvent[],
    eventsByChannelNameCount: number,
    resultsByChannelNameCount: EventLogTotalResultCounts,
    eventLogParamsByChannelName: EventLogParamsByChannelName
): EventLogByChannelNameSuccessAction => ({
    type: ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_SUCCESS,
    payload: {
        eventLogByChannelName,
        eventsByChannelNameCount,
        resultsByChannelNameCount,
        eventLogByChannelNameLoading: false,
        eventLogParamsByChannelName,
    },
});

export const ContactLinksLoadStart: ActionCreator<ContactLinksFetchStartAction> = (
    params: SingleContactPaginationInterface
): ContactLinksFetchStartAction => ({
    type: ReportsActionTypes.FETCH_CONTACT_LINKS_START,
    payload: {
        params,
        isLoadingLinks: true,
    },
});

export const ContactLinksLoadError: ActionCreator<ContactLinksFetchErrorAction> = (): ContactLinksFetchErrorAction => ({
    type: ReportsActionTypes.FETCH_CONTACT_LINKS_ERROR,
    payload: {
        isLoadingLinks: false,
    },
});

export const ContactLinksLoadSuccess: ActionCreator<ContactLinksFetchSuccessAction> = (
    links: LinksClicked[],
    linkCounts: number
): ContactLinksFetchSuccessAction => ({
    type: ReportsActionTypes.FETCH_CONTACT_LINKS_SUCCESS,
    payload: {
        links,
        isLoadingLinks: false,
        linkCounts,
    },
});

export const GetReportsSummaryStart: ActionCreator<GetReportsSummaryStartAction> = (
    params: ReportsSummaryParams
): GetReportsSummaryStartAction => ({
    type: ReportsActionTypes.GET_REPORTS_SUMMARY_START,
    payload: {
        from: params.from,
        to: params.to,
        channelName: params.channelName,
        interval: params.interval,
        transactionID: params.transactionID,
        reportsSummaryLoading: true,
    },
});
export const GetReportsSummarySuccess: ActionCreator<GetReportsSummarySuccessAction> = (
    reportsSummary: LogSummary
): GetReportsSummarySuccessAction => ({
    type: ReportsActionTypes.GET_REPORTS_SUMMARY_SUCCESS,
    payload: {
        reportsSummary,
        reportsSummaryLoading: false,
    },
});
export const GetReportsSummaryError: ActionCreator<
    GetReportsSummaryErrorAction
> = (): GetReportsSummaryErrorAction => ({
    type: ReportsActionTypes.GET_REPORTS_SUMMARY_ERROR,
    payload: {
        reportsSummaryLoading: false,
    },
});

export const ClearCampaignsReportsSummary: ActionCreator<
    ClearCampaignsReportsSummaryAction
> = (): ClearCampaignsReportsSummaryAction => ({
    type: CampaignsActionTypes.CLEAR_CAMPAIGN_REPORTS_SUMMARY,
});

export const ClearCampaignsLinkTracking: ActionCreator<
    ClearCampaignsLinkTrackingAction
> = (): ClearCampaignsLinkTrackingAction => ({
    type: ReportsActionTypes.CLEAR_CAMPAIGN_LINK_TRACKING,
});

export const ClearCampaignsReportsData: ActionCreator<
    ClearCampaignsReportsDataAction
> = (): ClearCampaignsReportsDataAction => ({
    type: ReportsActionTypes.CLEAR_CAMPAIGN_REPORTS_DATA,
});

export interface LinkTrackingParams {
    linkSearch?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    channelName: string;
    activePage?: number;
    perPage?: number;
    /**
     * if false/ not passed - from, to, limit, offset, activePage and perPage will be ignored
     */
    isRequiredFullDateRange: boolean;
}

export const GetLinkTrackingListStart: ActionCreator<GetLinkTrackingListStartAction> = (
    params: LinkTrackingParams
): GetLinkTrackingListStartAction => ({
    type: ReportsActionTypes.LOAD_LINK_TRACKING_START,
    payload: {
        ...params,
    },
});
export const GetLinkTrackingListSuccess: ActionCreator<GetLinkTrackingListSuccessAction> = (
    linkTrackingList: TrackedLink[],
    totalLinkCount: number,
    linkTrackingActivePage: number,
    linkTrackingPerPage: number
): GetLinkTrackingListSuccessAction => ({
    type: ReportsActionTypes.LOAD_LINK_TRACKING_SUCCESS,
    payload: {
        linkTrackingList,
        totalLinkCount,
        linkTrackingActivePage,
        linkTrackingPerPage,
        linkTrackingLoading: false,
    },
});
export const GetLinkTrackingListError: ActionCreator<
    GetLinkTrackingLisErrorAction
> = (): GetLinkTrackingLisErrorAction => ({
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ERROR,
    payload: {
        linkTrackingLoading: false,
    },
});

export const ExportLinks: ActionCreator<ExportLinksAction> = (params: ExportLinksParams): ExportLinksAction => ({
    type: ReportsActionTypes.EXPORT_LINKS,
    payload: {
        ...params,
    },
});

export const GetLinkTrackingAbSplitStart: ActionCreator<GetLinkTrackingAbSplitStartAction> = (
    params: LinktrackingsAbsplitActionParams
): GetLinkTrackingAbSplitStartAction => ({
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_START,
    payload: params,
});
export const GetLinkTrackingAbSplitSuccess: ActionCreator<GetLinkTrackingAbSplitSuccessAction> = (
    collection: TemplateLink[],
    totalCount: number,
    offset: number,
    limit: number
): GetLinkTrackingAbSplitSuccessAction => ({
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_SUCCESS,
    payload: {
        collection,
        totalCount,
        offset,
        limit,
        loading: false,
    },
});
export const GetLinkTrackingAbSplitError: ActionCreator<
    GetLinkTrackingAbSplitErrorAction
> = (): GetLinkTrackingAbSplitErrorAction => ({
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_ERROR,
    payload: {
        loading: false,
    },
});

export const LoadLinkContactsStart: ActionCreator<LinkContactsStartAction> = (
    params: LinkContactsParams
): LinkContactsStartAction => ({
    type: ReportsActionTypes.LINK_CONTACTS_START,
    payload: {
        ...params,
        linkContactsLoading: true,
    },
});
export const LoadLinkContactsSuccess: ActionCreator<LinkContactsSuccessAction> = (
    linkContactsList: LinkContacts,
    totalLinkContactsCount: number,
    linkContactsPagination: PaginationInterface
): LinkContactsSuccessAction => ({
    type: ReportsActionTypes.LINK_CONTACTS_SUCCESS,
    payload: {
        linkContactsList,
        totalLinkContactsCount,
        linkContactsPagination,
        linkContactsLoading: false,
    },
});
export const LoadLinkContactsError: ActionCreator<LinkContactsErrorAction> = (): LinkContactsErrorAction => ({
    type: ReportsActionTypes.LINK_CONTACTS_ERROR,
    payload: {
        linkContactsLoading: false,
    },
});

export const LoadLinkContactsAllStart: ActionCreator<LinkContactsAllStartAction> = (
    params: LinkContactsParams
): LinkContactsAllStartAction => ({
    type: ReportsActionTypes.LINK_CONTACTS_ALL_START,
    payload: {
        ...params,
        linkContactsAllLoading: true,
    },
});
export const LoadLinkContactsAllSuccess: ActionCreator<LinkContactsAllSuccessAction> = (
    linkContactsAllList: LinkContacts
): LinkContactsAllSuccessAction => ({
    type: ReportsActionTypes.LINK_CONTACTS_ALL_SUCCESS,
    payload: {
        linkContactsAllList,
    },
});
export const LoadLinkContactsAllError: ActionCreator<LinkContactsAllErrorAction> = (): LinkContactsAllErrorAction => ({
    type: ReportsActionTypes.LINK_CONTACTS_ALL_ERROR,
});

export const ExportLinkContacts: ActionCreator<ExportLinkContactsAction> = (
    params: ExportLinkContactsParams
): ExportLinkContactsAction => ({
    type: ReportsActionTypes.EXPORT_LINK_CONTACTS,
    payload: params,
});

export const GetReportsLocationStatsByName: ActionCreator<GetReportsLocationStatsByNameStartAction> = (
    params: LocationReportsInterface
): GetReportsLocationStatsByNameStartAction => ({
    type: ReportsActionTypes.GET_REPORTS_LOCATION_STATS_BY_NAME_START,
    payload: {
        ...params,
    },
});

export const GetReportsLocationStatsByNameSuccess: ActionCreator<GetReportsLocationStatsByNameSuccessAction> = (
    locationStatsData: ReportLocationStats
): GetReportsLocationStatsByNameSuccessAction => ({
    type: ReportsActionTypes.GET_REPORTS_LOCATION_STATS_BY_NAME_SUCCESS,
    payload: {
        locationStatsData,
    },
});

export const ClearLogFromReports: ActionCreator<ClearLogFromReportsAction> = (): ClearLogFromReportsAction => ({
    type: ReportsActionTypes.CLEAR_LOG,
});

export const ExportEvents: ActionCreator<ExportEventsAction> = (params: ExportEventsParams): ExportEventsAction => ({
    type: ReportsActionTypes.EXPORT_EVENTS,
    payload: params,
});

export const ResendStart: ActionCreator<ResendStartAction> = (params: ResendParams): ResendStartAction => ({
    type: ReportsActionTypes.RESEND_START,
    payload: {
        params,
    },
});

export const ResendError: ActionCreator<ResendErrorAction> = (): ResendErrorAction => ({
    type: ReportsActionTypes.RESEND_ERROR,
});

export const ResendSuccess: ActionCreator<ResendSuccessAction> = (): ResendSuccessAction => ({
    type: ReportsActionTypes.RESEND_SUCCESS,
});

export const GetChannelEventsCounts = (params: EventLogParamsByChannelName): FetchChannelEventsCountsAction => ({
    type: ReportsActionTypes.FETCH_CHANNEL_EVENTS_COUNTS,
    payload: params,
});

export const UpdateChannelEventsCounts = (eventsCounts: Record<string, any>): UpdateChannelEventsCountsAction => ({
    type: ReportsActionTypes.UPDATE_CHANNEL_EVENT_COUNTS,
    payload: { eventsCounts },
});

export const getReportsState = (state: ApplicationState): ReportsState => state.reports;
export const getDailyLogStatusSummary = (state: ApplicationState) =>
    state?.reports?.reportsSummary?.DailyLogStatusSummary;
